// Derived in-app alerts (no persistence yet). Cheap client-side rules
// over existing tables, scoped to the user's agency.
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export type AlertSeverity = 'info' | 'warn' | 'success' | 'critical';
export type AlertKind =
  | 'creator_dropoff'
  | 'low_posting'
  | 'sample_followup'
  | 'live_inactivity'
  | 'import_failed'
  | 'top_spike'
  | 'onboarding_setup';

export interface OperationalAlert {
  id: string;
  kind: AlertKind;
  severity: AlertSeverity;
  title: string;
  body: string;
  brandId?: string | null;
  brandName?: string | null;
  href?: string;
  ts: string;
}

const dayMs = 24 * 60 * 60 * 1000;

export function useOperationalAlerts(agencyId: string | null | undefined) {
  return useQuery({
    queryKey: ['op-alerts', agencyId],
    enabled: !!agencyId,
    staleTime: 60_000,
    queryFn: async (): Promise<OperationalAlert[]> => {
      const now = Date.now();
      const since14 = new Date(now - 14 * dayMs).toISOString();
      const since21 = new Date(now - 21 * dayMs).toISOString();
      const since7 = new Date(now - 7 * dayMs).toISOString();

      const [brandsRes, uploadsRes, samplesRes, liveRes, contentRes] = await Promise.all([
        // Pull status so we can scope lifecycle-sensitive alerts. Archived /
        // deleted brands are excluded entirely so the operator never sees
        // noisy alerts for stuff that's been turned off.
        supabase.from('brands').select('id, name, status').eq('agency_id', agencyId!).is('deleted_at', null).neq('status', 'archived'),
        supabase.from('tiktok_shop_uploads' as any).select('id, brand_id, file_name, status, created_at')
          .eq('agency_id', agencyId!).gte('created_at', since7).eq('status', 'failed'),
        supabase.from('sample_tracking').select('id, brand_id, product_name, status, shipped_at, affiliate_id')
          .eq('agency_id', agencyId!).eq('status', 'shipped').lt('shipped_at', new Date(now - 14 * dayMs).toISOString()),
        supabase.from('live_sessions').select('brand_id, started_at')
          .eq('agency_id', agencyId!).gte('started_at', since21),
        supabase.from('content_items').select('id, brand_id, title, gmv, posted_at, video_url, creator_handle')
          .eq('agency_id', agencyId!).gte('posted_at', since7).order('gmv', { ascending: false }).limit(40),
      ]);

      const brandsById = new Map((brandsRes.data ?? []).map(b => [b.id, b]));
      const activeBrandIds = (brandsRes.data ?? []).filter(b => b.status === 'active').map(b => b.id);
      const onboardingBrands = (brandsRes.data ?? []).filter(b => b.status === 'onboarding');

      // "Has ever run a LIVE" — needed so we don't fire live_inactivity on
      // brand-new brands that simply haven't launched yet.
      let brandsEverLive = new Set<string>();
      if (activeBrandIds.length > 0) {
        const { data: anyLive } = await supabase
          .from('live_sessions')
          .select('brand_id')
          .in('brand_id', activeBrandIds)
          .not('started_at', 'is', null)
          .limit(2000);
        brandsEverLive = new Set((anyLive ?? []).map((r: any) => r.brand_id).filter(Boolean));
      }

      // Per-brand setup signals for onboarding brands (team, campaigns,
      // uploads, creators). Single bulk fetch per signal, scoped by brand id.
      const onboardingIds = onboardingBrands.map(b => b.id);
      const [membersRes, campaignsRes, anyUploadsRes, affiliatesRes] = onboardingIds.length
        ? await Promise.all([
            supabase.from('brand_members' as any).select('brand_id').in('brand_id', onboardingIds),
            supabase.from('campaigns' as any).select('brand_id').in('brand_id', onboardingIds),
            supabase.from('tiktok_shop_uploads' as any).select('brand_id').in('brand_id', onboardingIds).limit(1000),
            supabase.from('affiliates').select('brand_id').in('brand_id', onboardingIds).limit(2000),
          ])
        : [{ data: [] }, { data: [] }, { data: [] }, { data: [] }] as any;
      const has = (rows: any[] | null | undefined) => {
        const s = new Set<string>();
        (rows ?? []).forEach((r: any) => r?.brand_id && s.add(r.brand_id));
        return s;
      };
      const hasTeam = has(membersRes.data);
      const hasCampaign = has(campaignsRes.data);
      const hasUpload = has(anyUploadsRes.data);
      const hasCreators = has(affiliatesRes.data);

      const out: OperationalAlert[] = [];

      // Import failures
      (uploadsRes.data as any[] ?? []).forEach((u: any) => {
        out.push({
          id: `imp-${u.id}`,
          kind: 'import_failed',
          severity: 'critical',
          title: `Import failed — ${u.file_name}`,
          body: 'A TikTok Shop upload failed in the last 7 days. Open diagnostics to retry.',
          brandId: u.brand_id,
          brandName: brandsById.get(u.brand_id)?.name ?? null,
          href: u.brand_id ? `/brands/${u.brand_id}` : undefined,
          ts: u.created_at,
        });
      });

      // Sample follow-ups
      (samplesRes.data ?? []).forEach((s: any) => {
        out.push({
          id: `samp-${s.id}`,
          kind: 'sample_followup',
          severity: 'warn',
          title: `Sample needs follow-up — ${s.product_name}`,
          body: 'Shipped over 14 days ago with no creator activity logged. Nudge the creator.',
          brandId: s.brand_id,
          brandName: brandsById.get(s.brand_id)?.name ?? null,
          href: s.brand_id ? `/brands/${s.brand_id}` : undefined,
          ts: s.shipped_at,
        });
      });

      // LIVE inactivity — ONLY fires for Active brands that have previously
      // run at least one LIVE. Lead / Onboarding / Paused / Churned / Archived
      // brands and brands that have never gone LIVE are intentionally skipped
      // so newly created brands don't generate false alerts.
      const liveInLast21 = new Set((liveRes.data ?? []).map((r: any) => r.brand_id));
      activeBrandIds.forEach(id => {
        if (!brandsEverLive.has(id)) return;        // never launched a LIVE
        if (liveInLast21.has(id)) return;           // recently active
        const b = brandsById.get(id);
        if (!b) return;
        out.push({
          id: `live-${id}`,
          kind: 'live_inactivity',
          severity: 'warn',
          title: `${b.name} — no LIVE in 21 days`,
          body: 'Schedule a LIVE session to keep momentum on TikTok Shop.',
          brandId: id,
          brandName: b.name,
          href: `/brands/${id}`,
          ts: new Date().toISOString(),
        });
      });

      // Onboarding setup alerts — surface missing setup pieces instead of
      // LIVE inactivity for brands still being set up.
      onboardingBrands.forEach(b => {
        const issues: string[] = [];
        if (!hasTeam.has(b.id))      issues.push('No team assigned');
        if (!hasCampaign.has(b.id))  issues.push('No campaign created');
        if (!hasUpload.has(b.id))    issues.push('No reports uploaded');
        if (!hasCreators.has(b.id))  issues.push('No creators assigned');
        if (issues.length === 0) return;
        out.push({
          id: `onb-${b.id}`,
          kind: 'onboarding_setup',
          severity: 'info',
          title: `${b.name} — onboarding setup incomplete`,
          body: issues.join(' · '),
          brandId: b.id,
          brandName: b.name,
          href: `/brands/${b.id}`,
          ts: new Date().toISOString(),
        });
      });

      // Top performing spikes — videos with GMV > 3x avg of brand's last 7d videos
      const byBrand = new Map<string, any[]>();
      (contentRes.data ?? []).forEach((c: any) => {
        if (!c.brand_id) return;
        const arr = byBrand.get(c.brand_id) ?? [];
        arr.push(c);
        byBrand.set(c.brand_id, arr);
      });
      byBrand.forEach((items, brandId) => {
        const gmvs = items.map(i => Number(i.gmv) || 0).filter(n => n > 0);
        if (gmvs.length < 3) return;
        const avg = gmvs.reduce((a, b) => a + b, 0) / gmvs.length;
        items.forEach(i => {
          const g = Number(i.gmv) || 0;
          if (g > avg * 3 && g > 100) {
            out.push({
              id: `spike-${i.id}`,
              kind: 'top_spike',
              severity: 'success',
              title: `Top performer spike — ${i.creator_handle ?? 'video'}`,
              body: `$${Math.round(g).toLocaleString()} GMV (>3× brand avg). Consider boosting or sparking.`,
              brandId,
              brandName: brandsById.get(brandId)?.name ?? null,
              href: i.video_url ?? (brandId ? `/brands/${brandId}` : undefined),
              ts: i.posted_at ?? new Date().toISOString(),
            });
          }
        });
      });

      // Sort newest first
      out.sort((a, b) => new Date(b.ts).getTime() - new Date(a.ts).getTime());
      return out;
    },
  });
}
